import { createAsyncThunk, createSlice } from "@reduxjs/toolkit"
import {
  getAllFilesOfEvents,
  getFoldersOfEvent,
  getPhysicalAlbumFiles,
  listPhysicalAlbums,
} from "../api"
import { PhysicalAlbum } from "../types/physicalAlbum"
import { toast } from "sonner"

// Get All Files
export const getAllFiles = createAsyncThunk(
  "getAllFiles",
  async (data: { eventId: string }, { rejectWithValue }) => {
    try {
      const response = await getAllFilesOfEvents(data?.eventId)
      return response
    } catch (error: any) {
      toast.error("Error Fetching All Files")
      return rejectWithValue(error.response)
    }
  }
)

// Build a map of albumId -> files across all albums (for "in any album" markers)
export const getAlbumMemberships = createAsyncThunk(
  "getAlbumMemberships",
  async (data: { albums: PhysicalAlbum[]; userId?: string }, { rejectWithValue }) => {
    try {
      const entries = await Promise.all(
        (data?.albums || []).map(async (album) => {
          try {
            const res = await getPhysicalAlbumFiles(album.id, data?.userId)
            return [album.id, res?.status === "OK" ? res?.body || [] : []] as const
          } catch {
            return [album.id, []] as const
          }
        })
      )
      const memberships: Record<string, any[]> = {}
      entries.forEach(([id, files]) => {
        memberships[id] = files
      })
      return memberships
    } catch (error: any) {
      return rejectWithValue(error)
    }
  }
)

// Get all Physical Albums of an event (with file counts)
export const getPhysicalAlbums = createAsyncThunk(
  "getPhysicalAlbums",
  async (data: { eventId: string; userId?: string }, { dispatch, rejectWithValue }) => {
    try {
      const response = await listPhysicalAlbums(data?.eventId, data?.userId)
      // Refresh per-album membership so gallery markers / folder counts stay accurate
      if (response?.status === "OK" && Array.isArray(response?.body)) {
        dispatch(getAlbumMemberships({ albums: response.body, userId: data?.userId }))
      }
      return response
    } catch (error: any) {
      toast.error("Error Fetching Physical Albums")
      return rejectWithValue(error.response)
    }
  }
)

// Get the files of a single (currently open) Physical Album
export const getActiveAlbumFiles = createAsyncThunk(
  "getActiveAlbumFiles",
  async (data: { physicalAlbumId: string; userId?: string }, { rejectWithValue }) => {
    try {
      const response = await getPhysicalAlbumFiles(data?.physicalAlbumId, data?.userId)
      return response
    } catch (error: any) {
      toast.error("Error Fetching Album Files")
      return rejectWithValue(error.response)
    }
  }
)

// Get EventFolders
export const getEventFolders = createAsyncThunk(
  "getEventFolders",
  async (eventId: string, { rejectWithValue }) => {
    try {
      const response = await getFoldersOfEvent(eventId)
      return response
    } catch (error: any) {
      toast.error("Error Fetching Event Folders")
      return rejectWithValue(error.response)
    }
  }
)

const initialState: {
  eventFolders: any
  allFiles: any
  physicalAlbums: PhysicalAlbum[] | null
  activeAlbumFiles: any[] | null
  albumMemberships: Record<string, any[]>
  loading: boolean
  error: any
  status: string
} = {
  eventFolders: null,
  allFiles: null,
  physicalAlbums: null,
  activeAlbumFiles: null,
  albumMemberships: {},
  loading: false,
  error: null,
  status: "",
}

const gallerySlice = createSlice({
  name: "Gallery",
  initialState,
  reducers: {
    // logout state changes
    logoutGallery: (state) => {
      state = initialState
    },

    // set the files of the currently open album
    setActiveAlbumFiles: (state, action) => {
      state.activeAlbumFiles = action.payload
    },

    // optimistically remove files from the currently open album
    filterActiveAlbumFiles: (state, action) => {
      state.activeAlbumFiles = (state.activeAlbumFiles || []).filter(
        (file: { id: string }) => !action.payload.includes(file.id)
      )
    },

    // filter files from gallery
    filterGallery: (state, action) => {
      state.allFiles = state.allFiles.filter(
        (af: { id: string }) => !action.payload.includes(af.id)
      )
    },

    // move file to folder
    filterFileAndMove: (state, action) => {
      state.allFiles = state.allFiles.map(
        (af: { id: string; folderId: string }) => {
          if (action.payload.photos.includes(af.id)) {
            return {
              ...af,
              folderId: action.payload.folderId,
            }
          }
          return af
        }
      )
    },
  },
  extraReducers: (builder) => {
    // Get All Files
    builder.addCase(getAllFiles.pending, (state) => {
      state.status = "pending"
      state.loading = true
    })
    builder.addCase(getAllFiles.fulfilled, (state, action) => {
      state.loading = false
      state.status = action.payload.status
      state.error = action.payload.error
      state.allFiles = action.payload.body

      if (action.payload.error || action.payload.status === "ERROR")
        toast.error(
          action.payload.error ||
            action.payload.body ||
            "Error Fetching User Files"
        )
    })
    builder.addCase(getAllFiles.rejected, (state) => {
      state.error = "Error fetching All Files"
      state.loading = false
      state.status = "ERROR"
    })

    // Get Physical Albums (list)
    builder.addCase(getPhysicalAlbums.pending, (state) => {
      state.status = "pending"
      state.loading = true
    })
    builder.addCase(getPhysicalAlbums.fulfilled, (state, action) => {
      state.loading = false
      state.status = action.payload.status
      state.error = action.payload.error
      state.physicalAlbums = Array.isArray(action.payload.body)
        ? action.payload.body
        : []

      if (action.payload.error || action.payload.status === "ERROR")
        toast.error(
          action.payload.error ||
            action.payload.body ||
            "Error Fetching Physical Albums"
        )
    })
    builder.addCase(getPhysicalAlbums.rejected, (state) => {
      state.error = "Error fetching Physical Albums"
      state.loading = false
      state.status = "ERROR"
    })

    // Get the active album's files
    builder.addCase(getActiveAlbumFiles.pending, (state) => {
      state.status = "pending"
      state.loading = true
    })
    builder.addCase(getActiveAlbumFiles.fulfilled, (state, action) => {
      state.loading = false
      state.status = action.payload.status
      state.error = action.payload.error
      state.activeAlbumFiles = Array.isArray(action.payload.body)
        ? action.payload.body
        : []

      if (action.payload.error || action.payload.status === "ERROR")
        toast.error(
          action.payload.error ||
            action.payload.body ||
            "Error Fetching Album Files"
        )
    })
    builder.addCase(getActiveAlbumFiles.rejected, (state) => {
      state.error = "Error fetching Album Files"
      state.loading = false
      state.status = "ERROR"
    })

    // Album memberships (union of all albums' files)
    builder.addCase(getAlbumMemberships.fulfilled, (state, action) => {
      state.albumMemberships = action.payload || {}
    })

    // Get Event Folders
    builder.addCase(getEventFolders.pending, (state) => {
      state.status = "pending"
      state.loading = true
    })
    builder.addCase(getEventFolders.fulfilled, (state, action) => {
      state.loading = false
      state.status = action.payload.status
      state.error = action.payload.error
      state.eventFolders = action.payload.body

      if (action.payload.error || action.payload.status === "ERROR")
        toast.error(
          action.payload.error ||
            action.payload.body ||
            "Error Fetching Event Folders"
        )
    })
    builder.addCase(getEventFolders.rejected, (state) => {
      state.error = "Error fetching Event Folders"
      state.loading = false
      state.status = "ERROR"
    })
  },
})

export const {
  logoutGallery,
  setActiveAlbumFiles,
  filterActiveAlbumFiles,
  filterGallery,
  filterFileAndMove,
} = gallerySlice.actions
export default gallerySlice.reducer
